/**
 * Typed Orees Vision API client (CLAUDE.md: "A typed API client … bearer + base URL from
 * import.meta.env, handle 401/404"). No `any` — every response is typed from the contract.
 */
import { config } from "@/api/config";
import type {
  AnomalyCard,
  AnomalyStatus,
  ClipLink,
  KpiValue,
  Restaurant,
  ShiftTimeline,
  TableStatus,
} from "@/types/contract";
import {
  mockAnomalies,
  mockClip,
  mockKpis,
  mockRestaurants,
  mockTables,
  mockTimeline,
} from "@/api/mocks";
import { ApiError, NotFoundError, UnauthorizedError } from "@/api/errors";

// Re-export so existing `import { NotFoundError } from "@/api/client"` keeps working.
export { ApiError, NotFoundError, UnauthorizedError } from "@/api/errors";

// ── Core fetch ──────────────────────────────────────────────────────────────

interface RequestOptions {
  method?: "GET" | "POST";
  query?: Record<string, string | undefined>;
  signal?: AbortSignal;
}

function buildUrl(path: string, query?: RequestOptions["query"]): string {
  const url = new URL(
    config.apiBase.replace(/\/$/, "") + path,
    // base needed only when apiBase is relative (e.g. "/api/v1")
    window.location.origin,
  );
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined && v !== "") url.searchParams.set(k, v);
    }
  }
  return url.toString();
}

async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = {
    Accept: "application/json",
  };
  if (config.userToken) {
    headers["Authorization"] = `Bearer ${config.userToken}`;
  }

  const res = await fetch(buildUrl(path, opts.query), {
    method: opts.method ?? "GET",
    headers,
    signal: opts.signal,
  });

  if (res.status === 401) throw new UnauthorizedError();
  if (res.status === 404) throw new NotFoundError();
  if (!res.ok) {
    throw new ApiError(res.status, `Ошибка запроса (${res.status})`);
  }
  // 204/empty bodies are not expected on our GET endpoints, but guard anyway.
  const text = await res.text();
  return (text ? JSON.parse(text) : null) as T;
}

/** Decide whether to fall back to mocks: explicit flag, OR a network error in dev. */
function mocksAllowed(): boolean {
  return config.useMocks || import.meta.env.DEV;
}

function isNetworkError(e: unknown): boolean {
  return e instanceof TypeError; // fetch throws TypeError on network failure
}

// ── Endpoints ───────────────────────────────────────────────────────────────

export interface AnomaliesQuery {
  restaurant_id?: string;
  status?: AnomalyStatus;
  since?: string;
}

export const api = {
  /** GET /anomalies — the feed. */
  async getAnomalies(
    q: AnomaliesQuery = {},
    signal?: AbortSignal,
  ): Promise<AnomalyCard[]> {
    if (config.useMocks) return filterMockAnomalies(q);
    try {
      return await request<AnomalyCard[]>("/anomalies", {
        query: { ...q },
        signal,
      });
    } catch (e) {
      if (isNetworkError(e) && mocksAllowed()) return filterMockAnomalies(q);
      throw e;
    }
  },

  /** GET /anomalies/{id}/clip — signed, TTL-limited link. 404 ⇒ NotFoundError. */
  async getClip(anomalyId: string, signal?: AbortSignal): Promise<ClipLink> {
    if (config.useMocks) return mockClip(anomalyId);
    try {
      return await request<ClipLink>(
        `/anomalies/${encodeURIComponent(anomalyId)}/clip`,
        { signal },
      );
    } catch (e) {
      if (isNetworkError(e) && mocksAllowed()) return mockClip(anomalyId);
      throw e;
    }
  },

  /** GET /metrics/kpi — KPI values for a period. */
  async getKpis(
    q: { restaurant_id?: string; from?: string; to?: string } = {},
    signal?: AbortSignal,
  ): Promise<KpiValue[]> {
    if (config.useMocks) return mockKpis;
    try {
      return await request<KpiValue[]>("/metrics/kpi", { query: { ...q }, signal });
    } catch (e) {
      if (isNetworkError(e) && mocksAllowed()) return mockKpis;
      throw e;
    }
  },

  /** Точки, доступные роли пользователя (manager → своя, network → сеть). */
  async getRestaurants(signal?: AbortSignal): Promise<Restaurant[]> {
    if (config.useMocks) return mockRestaurants;
    try {
      return await request<Restaurant[]>("/restaurants", { signal });
    } catch (e) {
      if (isNetworkError(e) && mocksAllowed()) return mockRestaurants;
      throw e;
    }
  },

  async getTables(
    q: { restaurant_id?: string } = {},
    signal?: AbortSignal,
  ): Promise<TableStatus[]> {
    if (config.useMocks) return mockTables;
    try {
      return await request<TableStatus[]>("/tables", { query: { ...q }, signal });
    } catch (e) {
      if (isNetworkError(e) && mocksAllowed()) return mockTables;
      throw e;
    }
  },

  /** GET /tables/timeline — смена: столы × заказы (факт-чек сшивки зона↔стол). */
  async getTableTimeline(
    q: { restaurant_id?: string } = {},
    signal?: AbortSignal,
  ): Promise<ShiftTimeline> {
    if (config.useMocks) return mockTimeline();
    try {
      return await request<ShiftTimeline>("/tables/timeline", {
        query: { ...q },
        signal,
      });
    } catch (e) {
      if (isNetworkError(e) && mocksAllowed()) return mockTimeline();
      throw e;
    }
  },

  /**
   * Acknowledge / resolve an anomaly via PATCH /anomalies/{id} { status } — as
   * defined in the OpenAPI core spec and implemented by AnomalyController.update.
   */
  async setAnomalyStatus(
    anomalyId: string,
    status: AnomalyStatus,
  ): Promise<void> {
    if (config.useMocks) return;
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      Accept: "application/json",
    };
    if (config.userToken) headers["Authorization"] = `Bearer ${config.userToken}`;

    let res: Response;
    try {
      res = await fetch(buildUrl(`/anomalies/${encodeURIComponent(anomalyId)}`), {
        method: "PATCH",
        headers,
        body: JSON.stringify({ status }),
      });
    } catch (e) {
      // Network failure: in dev / with mocks the optimistic update stands.
      if (isNetworkError(e) && mocksAllowed()) return;
      throw e;
    }
    if (res.status === 401) throw new UnauthorizedError();
    if (res.status === 404) throw new NotFoundError();
    if (!res.ok) {
      if (mocksAllowed()) return;
      throw new ApiError(res.status, `Не удалось обновить статус (${res.status})`);
    }
  },
};

function filterMockAnomalies(q: AnomaliesQuery): AnomalyCard[] {
  return mockAnomalies.filter((a) => {
    if (q.restaurant_id && a.restaurant_id !== q.restaurant_id) return false;
    if (q.status && a.status !== q.status) return false;
    if (q.since && a.created_at < q.since) return false;
    return true;
  });
}
